import { defaultParamsSerializer, handleApiResponse, notifyInternalMessage } from '@/functions';
import { ICreator, IFetchDataParams, IPaymentInfo } from '@/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import { Paginated } from '@/lib/interface';
import { GenderEnum } from '@/lib/enum';
import routes from '@/lib/routes';
import dayjs from 'dayjs';

const DEFAULT_REFERRAL_COINS = 300;

type TResponseData = Paginated<ICreator>;
type TParams = IFetchDataParams & {
  gender?: GenderEnum[];
  createdAtFrom?: Date;
  createdAtTo?: Date;
  email?: string;
  ids?: string[];
  name?: string;
};

const VARIABLES = {
  convertResponseData: (responseData?: TResponseData) => {

    if (!responseData) {
      return responseData;
    }

    return {
      ...responseData,
      data: (
        [...(responseData?.data ?? [])]
          .map(
            l => {
              const extra = (l as any).extra;
              return ({
                ...l,
                paymentInfo: extra.paymentInfo as IPaymentInfo,
                createdAt: dayjs(l.createdAt).toDate(),
                links: (extra.links ?? []) as string[],
                referralCode: extra.referralCode,
                type: extra.type,
                coinsAmount: extra.referralCoins ?? DEFAULT_REFERRAL_COINS
              });
            }
          )
      )
    };

  },
  defaultData: {
    data: [],
    pagination: {
      hasPreviousPage: false,
      hasNextPage: false,
      itemCount: 0,
      pageCount: 0,
      limit: 500,
      page: 1
    }
  } as TResponseData
};

export const useRequestGetCreators = (params?: TParams, run = true) => {

  const { invalidate } = useInvalidateQueries();
  const { isFetching, data, error } = useQuery({
    queryKey: [routes.api.referrals, params, run],
    queryFn: ({ queryKey: [_url, _params, _run] }) => (
      !_run
        ? Promise.resolve(null)
        : apiClient
          .get<TResponseData>(
            _url as string,
            _params as TParams,
            { paramsSerializer: defaultParamsSerializer }
          )
          .then(handleApiResponse)
          .then(VARIABLES.convertResponseData)
          .catch(notifyInternalMessage)
    )
  });

  const dataResult = data ?? VARIABLES.defaultData;
  const creators = dataResult.data ?? [];

  const refresh = () =>
    invalidate(routes.api.referrals);

  return ({
    isLoading: isFetching,
    data: dataResult,
    creators,
    refresh,
    error
  });

};
